import React, { useState } from "react";
import {
  Box,
  Typography,
  Container,
  Button,
  Switch,
  SwitchProps,
  Grid,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useAFTheme } from "@theme/.";
import PricingICon from "@icons/PricingIcon";
import { useNavigate } from "react-router-dom";
import {
  ANNUALLY,
  MONTHLY,
  MOSTPOPULAR,
  PLAN,
  PRICING,
} from "@constants/unicomply-keys";
import {
  getPricingTiersWithRegionalFallback,
  PricingTier,
} from "@services/payment";
import {
  getSelectedCountryCode,
  getPricingLocaleFiltersForApi,
} from "@utils/countryUtils";

/** Align facility payload country with app sidebar (selectedCountry in localStorage). */
function normalizePricingCountryToken(raw: string | null | undefined): string {
  const s = String(raw ?? "")
    .trim()
    .toUpperCase();
  if (!s) return "";
  if (s === "US" || s === "USA" || s.includes("UNITED STATES")) return "USA";
  if (s === "CA" || s === "CAN" || s === "CANADA" || s.includes("CANADA"))
    return "CAN";
  if (s === "AE" || s === "UAE") return "AE";
  if (s === "IN" || s === "IND" || s.includes("INDIA")) return "IN";
  if (s === "GB" || s === "UK") return "GB";
  return s;
}

function facilityStorageMatchesSelectedCountry(payload: {
  country?: string;
  country_code?: string;
} | null | undefined): boolean {
  if (!payload) return true;
  const fc = normalizePricingCountryToken(
    payload.country_code || payload.country,
  );
  if (!fc) return true;
  const app = normalizePricingCountryToken(getSelectedCountryCode());
  return fc === app;
}

const PricingSection = styled(Box)(({ theme }) => ({
  backgroundColor: "#033843",
  padding: "100px 0",
  [theme.breakpoints.down("md")]: {
    padding: "60px 0",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "40px 0",
  },
}));

const PricingBadge = styled(Box)({
  // height: 7,
  // borderRadius: 14,
  // padding: "11px",
  // display: "flex",
  // alignItems: "center",
  // gap: 7,
  // border: "1px solid #0A829D",
  // background: "linear-gradient(179deg, #FFF 83.87%, #F5F5F5 125.73%)",
  // boxShadow: "0 1px 2px 0 rgba(82, 88, 102, 0.06)",
  // justifyContent: "center",
  display: "inline-flex", // shrink to content
  alignItems: "center",
  justifyContent: "center",
  gap: 7,
  padding: "4px 8px", // less padding so height matches text
  borderRadius: 14,
  border: "1px solid #0A829D",
  background: "linear-gradient(179deg, #FFF 83.87%, #F5F5F5 125.73%)",
  // background:

  //   "var(--Gradlient-1, linear-gradient(94deg, var(--Brand-Colors-color--grad-1, #35D7E4) -2.59%, var(--Primary-Color-Palette-500, #0A829D) 115.51%))",
  boxShadow: "0 1px 2px 0 rgba(82, 88, 102, 0.06)",
});

const CustomSwitch = styled((props: SwitchProps) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 62,
  height: 38,
  padding: 7,
  "& .MuiSwitch-switchBase": {
    margin: 4,
    padding: 0,
    transform: "translateX(6px)",
    "&.Mui-checked": {
      color: "#fff",
      transform: "translateX(28px)",
      "& + .MuiSwitch-track": {
        backgroundColor: "#2B2B2B", // green when ON
        opacity: 1,
        border: 0,
      },
    },
  },
  "& .MuiSwitch-thumb": {
    background: "linear-gradient(135deg, #00c6ff, #0072ff)", // blue gradient knob
    width: 23,
    height: 23,
    borderRadius: "50%",
    marginTop: 3,
  },
  "& .MuiSwitch-track": {
    borderRadius: 20,
    backgroundColor: "#333", // dark background
    opacity: 1,
    transition: theme.transitions.create(["background-color"], {
      duration: 500,
    }),
  },
}));

const PricingCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isPopular",
})<{ isPopular?: boolean }>(({ isPopular = false, theme }) => ({
    position: "relative",
    backgroundColor: "#ffffff",
    borderRadius: 8,
    padding: 24,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    height: "92%",
    minHeight: "480px",
    border: isPopular ? "2px solid #c3f4f8" : "2px solid #e3f2fd",
    boxShadow:
      "0px 2px 19.4px rgba(118, 223, 247, 0.3), 0px 1px 2px rgba(0, 0, 0, 0.05)",
    justifyContent: "flex-start",
    [theme.breakpoints.down("md")]: {
      minHeight: "420px",
      padding: 20,
    },
    [theme.breakpoints.down("sm")]: {
      minHeight: "380px",
      padding: 16,
      margin: "0 8px",
    },
  }));

const PopularBadge = styled(Box)({
  position: "absolute",
  top: -14,
  left: 36,
  background:
    "var(--Gradlient-1, linear-gradient(94deg, var(--Brand-Colors-color--grad-1, #35D7E4) -2.59%, var(--Primary-Color-Palette-500, #0A829D) 115.51%))",
  color: "var(--White, var(--White, #FFF))",
  padding: "6px 16px",
  borderRadius: 8,
  fontFamily: "Rethink Sans",
  fontSize: 12,
  fontStyle: "normal",
  fontWeight: 700,
  lineHeight: "16px",
  letterSpacing: "0.3px",
  textTransform: "uppercase",
});

const FeatureItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isSpecial",
})<{ isSpecial?: boolean }>(({ isSpecial = false }) => ({
    display: "flex",
    alignItems: "center",
    gap: 12,
    width: "100%",
    marginBottom: 24,
  }));

const FeatureIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isSpecial",
})<{ isSpecial?: boolean }>(({ isSpecial = false }) => ({
    width: 24,
    height: 24,
    flexShrink: 0,
    "& svg": {
      width: "100%",
      height: "100%",
      fill: isSpecial ? "#ffd700" : "#10b981",
    },
  }));

const PricingButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "btnVariant",
})<{ btnVariant: "primary" | "secondary" }>(({ btnVariant }) => ({
    width: "100%",
    padding: "12px 20px",
    borderRadius: 8,
    fontWeight: 600,
    fontSize: 16,
    textTransform: "none",
    ...(btnVariant === "primary"
      ? {
          backgroundColor: "#065b70",
          color: "#ffffff",
          "&:hover": {
            backgroundColor: "#044a5a",
          },
        }
      : {
          border: "1px solid #065b70",
          color: "#065b70",
          backgroundColor: "#ffffff",
          "&:hover": {
            backgroundColor: "#065b70",
            color: "#ffffff",
          },
        }),
  }));

const BillingToggle = styled(Box)({
  display: "flex",
  padding: "0 41px",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "8px",
  alignSelf: "stretch",
});

// Check icon SVG
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
  </svg>
);

// Star icon SVG
const StarIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

// Pricing icon SVG

interface FeatureItemProps {
  text: string;
  isSpecial?: boolean;
}

function FeatureItemComponent({ text, isSpecial = false }: FeatureItemProps) {
  return (
    <FeatureItem isSpecial={isSpecial}>
      <FeatureIcon isSpecial={isSpecial}>
        {isSpecial ? <StarIcon /> : <CheckIcon />}
      </FeatureIcon>
      <Typography
        sx={{
          fontWeight: 400,
          color: "#6b7280",
          fontSize: 16,
          lineHeight: "24px",
          flex: 1,
        }}
      >
        {text}
      </Typography>
    </FeatureItem>
  );
}

interface PricingCardProps {
  title: string;
  price: string;
  period?: string;
  description: string;
  features: string[];
  isPopular?: boolean;
  buttonText: string;
  buttonVariant: "primary" | "secondary";
  specialFeatureIndex?: number;
  onButtonClick: () => void;
  billingLabel?: string;
}

function PricingCardComponent({
  title,
  price,
  period,
  description,
  features,
  isPopular = false,
  buttonText,
  buttonVariant,
  specialFeatureIndex,
  onButtonClick,
  billingLabel,
}: PricingCardProps) {
  return (
    <PricingCard isPopular={isPopular}>
      {isPopular && <PopularBadge>{MOSTPOPULAR}</PopularBadge>}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: "100%",
          position: "relative",
        }}
      >
        {/* Header */}
        <Box sx={{ marginBottom: 3 }}>
          <Typography
            sx={{
              color: "var(--Gray-6, var(--Gray-5, #101828))",
              fontSize: "20px",
              fontStyle: "normal",
              fontWeight: 600,
              lineHeight: "28px",
              mb: 2,
            }}
          >
            {title}
          </Typography>

          {/* Price */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              flexShrink: 0,
              height: "60px",
              gap: 1,
            }}
          >
            <Typography
              sx={{
                color: "var(--Gray-6, var(--Gray-5, #101828))",
                fontSize: "35px",
                fontStyle: "normal",
                fontWeight: 600,
                lineHeight: "60px",
                letterSpacing: "-0.96px",
              }}
            >
              {price}
            </Typography>
          </Box>

          {/* Period and Billing Label under price */}
          {(period || billingLabel) && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0, mb: 1 }}>
              {period && (
                <Typography
                  sx={{
                    color: "var(--Gray-6, var(--Gray-5, #101828))",
                    fontSize: "14px",
                    fontStyle: "normal",
                    fontWeight: 500,
                    lineHeight: "18px",
                  }}
                >
                  {period}
                </Typography>
              )}
            </Box>
          )}

          {/* Description removed as per requirement */}
        </Box>

        {/* Features List - Fixed height container for alignment */}
        <Box sx={{ 
          display: "flex", 
          flexDirection: "column", 
          flex: 1,
          minHeight: "200px",
          paddingRight: 2
        }}>
          {features.map((feature, index) => (
            <FeatureItemComponent
              key={index}
              text={feature}
            />
          ))}
        </Box>

        {/* Button - Always at bottom */}
        <Box sx={{ marginTop: "auto", paddingTop: 2 }}>
          <PricingButton btnVariant={buttonVariant} onClick={onButtonClick}>
            {buttonText}
          </PricingButton>
        </Box>
      </Box>
    </PricingCard>
  );
}

export default function UniComply_Pricing() {
  useAFTheme();
  const [isMonthly, setIsMonthly] = useState(false);
  const navigate = useNavigate();

  const [pricingTiers, setPricingTiers] = useState<PricingTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get facility information from sessionStorage
  const [facilityInfo, setFacilityInfo] = React.useState<any>(null);
  const [userEmail, setUserEmail] = React.useState<string>("");

  React.useEffect(() => {
    const loadFacilityInfo = () => {
      try {
        let filled = false;
        const preferredFacility = sessionStorage.getItem(
          "preferred_facility_for_pricing",
        );
        console.log("📋 Raw preferred_facility_for_pricing:", preferredFacility);

        if (preferredFacility) {
          const parsed = JSON.parse(preferredFacility);
          console.log("📋 Parsed preferred facility:", parsed);
          const details = parsed.facility_details ?? {};
          const countryPayload = {
            country:
              (details as { country?: string }).country ??
              (parsed as { country?: string }).country,
            country_code:
              (details as { country_code?: string }).country_code ??
              (parsed as { country_code?: string }).country_code,
          };
          if (!facilityStorageMatchesSelectedCountry(countryPayload)) {
            console.warn(
              "⚠️ Ignoring preferred_facility_for_pricing: country does not match app selection",
            );
          } else {
            const facilityName = parsed.facility_name || parsed.site_name || "";
            const facilityType = parsed.facility_type || "";

            console.log("🏥 Extracted:", { facilityName, facilityType });

            if (facilityName || facilityType) {
              setFacilityInfo({
                facility_name: facilityName,
                facility_type: facilityType,
              });
              filled = true;
            }
          }
        }

        if (!filled) {
          const filteredFacility = sessionStorage.getItem(
            "filtered_selectedFacility",
          );
          console.log("📋 Raw filtered_selectedFacility:", filteredFacility);

          if (filteredFacility) {
            const parsed = JSON.parse(filteredFacility);
            console.log("📋 Parsed filtered facility:", parsed);

            if (
              !facilityStorageMatchesSelectedCountry({
                country: parsed.country,
                country_code: parsed.country_code,
              })
            ) {
              console.warn(
                "⚠️ Ignoring filtered_selectedFacility: country does not match app selection",
              );
            } else {
              const facilityName =
                parsed.site_name || parsed.facility_name || "";
              const facilityType = parsed.facility_type || "";

              console.log("🏥 Extracted:", { facilityName, facilityType });

              if (facilityName || facilityType) {
                setFacilityInfo({
                  facility_name: facilityName,
                  facility_type: facilityType,
                });
                filled = true;
              }
            }
          }
        }

        if (!filled) {
          const facilityName = sessionStorage.getItem("sessionFacilityName");
          const facilityType = sessionStorage.getItem("sessionFacilityType");
          console.log("📋 Session keys:", { facilityName, facilityType });

          if (facilityName || facilityType) {
            setFacilityInfo({
              facility_name: facilityName || "",
              facility_type: facilityType || "",
            });
            filled = true;
          }
        }

        if (!filled) {
          setFacilityInfo(null);
        }

        // Get user email
        const email = sessionStorage.getItem("userEmail") || "";
        setUserEmail(email);
        console.log("👤 User email:", email);
      } catch (err) {
        console.error("❌ Error reading facility info from sessionStorage:", err);
      }
    };

    // Load immediately
    loadFacilityInfo();

    // Also listen for storage changes
    const handleStorageChange = () => {
      console.log("🔄 Storage changed, reloading facility info");
      loadFacilityInfo();
    };

    window.addEventListener("storage", handleStorageChange);
    
    // Also poll for changes every 500ms (for same-tab updates)
    const pollInterval = setInterval(loadFacilityInfo, 500);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(pollInterval);
    };
  }, []);

  React.useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const filters: Parameters<typeof getPricingTiersWithRegionalFallback>[0] = {
          frequency: isMonthly ? "monthly" : "yearly",
          sku_name: "SKU.Unicomply.Rules",
          vertical: "healthcare",
          ...getPricingLocaleFiltersForApi(),
        };
        try {
          const ardcode = sessionStorage.getItem("ardcode");
          if (ardcode) filters.ardcode = ardcode;
        } catch (_) {}
        const response = await getPricingTiersWithRegionalFallback(filters);
        if (response.success) {
          setPricingTiers(response.data);
        } else {
          setError("Failed to load pricing data");
        }
      } catch (err) {
        console.error("Error loading pricing tiers:", err);
        setError("Failed to load pricing data");
      } finally {
        setLoading(false);
      }
    };

    loadData();

    let lastCountry = getSelectedCountryCode();
    const countryPoll = setInterval(() => {
      const now = getSelectedCountryCode();
      if (now !== lastCountry) {
        lastCountry = now;
        loadData();
      }
    }, 500);

    return () => clearInterval(countryPoll);
  }, [isMonthly]);

  const pricingPlans = pricingTiers
    .filter((tier) => tier.is_active === 1)
    .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
    .map((tier) => {
      const title = (tier as any).name || (tier as any).display_name || (tier as any).tier_name || "";

      // Prefer content_package_details and plan_details from new API
      const pkgFeatures = (tier as any).content_package_details?.map((pkg: any) => pkg.description || `${pkg.name} - ${pkg.unit}`) as string[] | undefined;
      const planData = (tier as any).plan_details?.[0]?.plan_data as string[] | undefined;
      const legacyFormatted = (tier as any).formatted_features as string[] | undefined;
      const legacyDisplay = (tier as any).display_features?.map((f: any) => f.name) as string[] | undefined;

      // Show plan_data explicitly instead of feature data; fall back to other sources if missing
      const features: string[] = (planData || pkgFeatures || legacyFormatted || legacyDisplay || []);

      const priceDisplay = (() => {
        if ((tier as any).is_free && (tier as any).redirect_to === "contactus") return "Contact Us";
        if ((tier as any).is_free) return "Free";
        const raw = (tier as any).price;
        const num = typeof raw === "number" ? raw : parseFloat(String(raw ?? ""));
        const displayNum =
          Number.isNaN(num)
            ? NaN
            : isMonthly
            ? num
            : num / 12;
        const normalized = Number.isNaN(displayNum)
          ? String(raw ?? "")
          : (displayNum % 1 === 0
              ? String(Math.trunc(displayNum))
              : String(displayNum.toFixed(2)));
        const symbol = (tier as any).region_currency_symbol || (tier as any).currency || "";
        return symbol ? `${symbol}${normalized}` : normalized;
      })();

      return {
        id: String(tier.id),
        title,
        price: priceDisplay,
        period: (tier as any).is_free ? undefined : ((tier as any).period || undefined),
        description: (tier as any).description || "",
        features,
        isPopular: (tier as any).is_popular,
        // Match prior visual intent: paid => primary, free => secondary
        buttonVariant: ((tier as any).is_free ? "secondary" : "primary") as "primary" | "secondary",
        buttonText: (tier as any).is_free && (tier as any).redirect_to === "contactus" ? "Contact Us" : ((tier as any).is_free ? "Try for free" : "Sign up"),
        isFree: (tier as any).is_free,
        redirectTo: (tier as any).redirect_to,
        specialFeatureIndex: undefined,
      };
    });

  if (loading) {
    return (
      <PricingSection id="pricing">
        <Container maxWidth="lg">
          <Typography sx={{ color: "#fff" }}>Loading pricing plans...</Typography>
        </Container>
      </PricingSection>
    );
  }

  if (error) {
    return (
      <PricingSection id="pricing">
        <Container maxWidth="lg">
          <Typography sx={{ color: "#ffdddd" }}>Error: {error}</Typography>
        </Container>
      </PricingSection>
    );
  }

  return (
    <PricingSection id="pricing">
      <Container maxWidth="lg">
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            marginBottom: 5,
          }}
        >
          {/* Pricing Badge */}
          <PricingBadge>
            <Box
              sx={{
                width: 12,
                height: 12,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <PricingICon />
            </Box>
            <Typography
              sx={{
                color: "var(--Brand-Colors-color-brand-primary, #0A829D)",
                fontSize: "12px",
                fontWeight: 500,
                lineHeight: "16px",
                letterSpacing: "-0.24px",
              }}
            >
              {PRICING}
            </Typography>
          </PricingBadge>

          {/* Title */}
          <Typography
            component="div"
            sx={{
              fontWeight: 600,
              fontSize: "37.48px",
              lineHeight: "normal",
              textAlign: "center",
            }}
          >
            <Box component="span" sx={{ color: "#35d7e4" }}>
              {PRICING}
            </Box>
            <Box component="span" sx={{ color: "#ffffff" }}>
              {" "}
              {PLAN}
            </Box>
          </Typography>
        </Box>

        {/* Facility Selection Info */}
        {facilityInfo && (facilityInfo.facility_name || facilityInfo.facility_type) && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 2,
              marginBottom: 3,
            }}
          >
            <Typography
              component="div"
              sx={{
                color: "#fff",
                fontFamily: '"Rethink Sans"',
                fontSize: "20px",
                fontStyle: "normal",
                fontWeight: 400,
                lineHeight: "normal",
              }}
            >
              <Box component="span">You have selected an</Box>
              {facilityInfo.facility_name && (
                <Box
                  component="span"
                  sx={{
                    fontWeight: 700,
                  }}
                >
                  {" "}
                  {facilityInfo.facility_name}
                </Box>
              )}
              {facilityInfo.facility_type && (
                <>
                  <Box component="span">, </Box>
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    {facilityInfo.facility_type}
                  </Box>
                </>
              )}
              {userEmail && (
                <Box component="div" sx={{ mt: 1 }}>
                  This facility should be attached to user
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    {" "}
                    {userEmail}
                  </Box>
                </Box>
              )}
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            marginBottom: 5,
          }}
        >
          {/* Billing Toggle */}
          <BillingToggle>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <Typography
                sx={{
                  color: isMonthly
                    ? "var(--Text-and-Icon-Inverse, #FFF)"
                    : "var(--Brand-Colors-color--grad-1, #35D7E4)",
                  textAlign: "center",
                  fontSize: "16px",
                  fontStyle: "normal",
                  fontWeight: 400,
                  lineHeight: "24px",
                }}
              >
                {ANNUALLY}
              </Typography>

              <CustomSwitch
                checked={isMonthly}
                onChange={(e) => setIsMonthly(e.target.checked)}
              />

              <Typography
                sx={{
                  color: isMonthly
                    ? "var(--Brand-Colors-color--grad-1, #35D7E4)"
                    : "var(--Text-and-Icon-Inverse, #FFF)",
                  textAlign: "center",
                  fontSize: "16px",
                  fontStyle: "normal",
                  fontWeight: 400,
                  lineHeight: "24px",
                }}
              >
                {MONTHLY}
              </Typography>
            </Box>
          </BillingToggle>
        </Box>

        {/* Pricing Cards */}
        <Grid container spacing={pricingPlans.length === 3 ? 3 : 1} justifyContent="center" alignItems="stretch">
          {pricingPlans.map((plan, index) => (
            <Grid 
              item 
              xs={12} 
              sm={pricingPlans.length === 3 ? 4 : 6} 
              md={pricingPlans.length === 3 ? 4 : 3} 
              key={index}
            >
              <PricingCardComponent
                title={plan.title}
                price={plan.price}
                period={plan.period}
                description={plan.description}
                features={plan.features}
                isPopular={plan.isPopular}
                buttonText={plan.buttonText}
                buttonVariant={plan.buttonVariant}
                specialFeatureIndex={plan.specialFeatureIndex}
                onButtonClick={() => {
                  if (plan.isFree && plan.redirectTo === "contactus") {
                    navigate("/ContactUs");
                  } else {
                    navigate("/signup");
                  }
                }}
              />
            </Grid>
          ))}
        </Grid>
      </Container>
    </PricingSection>
  );
}
